import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Search,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Layers,
  CreditCard,
  Calendar,
  Radio,
  Box,
  Building,
  SlidersHorizontal,
  ArrowRight,
  Sparkles,
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { lldProblemService, LLDProblemWithProgress } from '../../../services/lldProblemService';
import { lldCustomProblemService, CustomLLDProblem } from '../../../services/lldCustomProblemService';
import { useAuth } from '../../../context/AuthContext';
import DeleteConfirmDialog from '../../../components/lld/DeleteConfirmDialog';
import { filterProblems, Difficulty, Category, ProblemFilters } from '../../../utils/lldFilters';
import { calculateProgress } from '../../../utils/lldScoring';
import PageHeader from '../../../components/common/PageHeader';

const DIFFICULTIES: Difficulty[] = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];
const CATEGORIES: Category[] = [
  'Object Modeling',
  'System Management',
  'Payment and Splitting',
  'Booking and Reservation',
  'Real-Time Services',
];

const difficultyDot = (d: Difficulty): string => {
  switch (d) {
    case 'Beginner': return 'bg-emerald-500';
    case 'Intermediate': return 'bg-amber-500';
    case 'Advanced': return 'bg-orange-500';
    case 'Expert': return 'bg-rose-500';
    default: return 'bg-gray-400';
  }
};

// Subtle colored badge for difficulty — tasteful, not loud.
const difficultyBadge = (d: Difficulty): string => {
  switch (d) {
    case 'Beginner': return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
    case 'Intermediate': return 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
    case 'Advanced': return 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300';
    case 'Expert': return 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  }
};

const getCategoryIcon = (category: Category) => {
  switch (category) {
    case 'Object Modeling': return Box;
    case 'System Management': return Layers;
    case 'Payment and Splitting': return CreditCard;
    case 'Booking and Reservation': return Calendar;
    case 'Real-Time Services': return Radio;
    default: return Building;
  }
};

const ProblemCatalog: React.FC = () => {
  const navigate = useNavigate();
  const { state: authState } = useAuth();
  const [problems, setProblems] = useState<LLDProblemWithProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDifficulty, setSelectedDifficulty] = useState<Difficulty | undefined>(undefined);
  const [selectedCategory, setSelectedCategory] = useState<Category | undefined>(undefined);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // ─── "Your problems" — independent of the curated request ──────────────────────
  const [customProblems, setCustomProblems] = useState<CustomLLDProblem[]>([]);
  const [customLoading, setCustomLoading] = useState(true);
  const [customError, setCustomError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CustomLLDProblem | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const fetchCustom = useCallback(async () => {
    setCustomLoading(true);
    setCustomError(null);
    try {
      const data = await lldCustomProblemService.listMine();
      setCustomProblems(data);
    } catch (err: any) {
      console.error('Failed to fetch custom LLD problems:', err);
      setCustomError(err?.message || 'Failed to load your problems.');
    } finally {
      setCustomLoading(false);
    }
  }, []);

  useEffect(() => {
    // Curated catalog — drives the page header/stats.
    const fetchProblems = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await lldProblemService.getProblemsWithProgress();
        setProblems(data);
      } catch (err: any) {
        console.error('Failed to fetch LLD problems:', err);
        setError(err.message || 'Failed to load problems. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    // Fire both requests in parallel; they are independent so a failure in one
    // never blanks the other.
    fetchProblems();
    fetchCustom();
  }, [fetchCustom]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteError(null);
    const targetId = deleteTarget.id;
    try {
      await lldCustomProblemService.remove(targetId);
      // Optimistically drop the card from the list.
      setCustomProblems((prev) => prev.filter((p) => p.id !== targetId));
      setDeleteTarget(null);
    } catch (err: any) {
      setDeleteError(err?.message || 'Failed to delete. Please try again.');
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget]);

  const mappedProblems = useMemo(
    () =>
      problems.map((p) => ({
        id: p.id,
        title: p.title,
        difficulty: p.difficulty,
        category: p.category,
        description: p.description,
        isCompleted: p.progress?.isCompleted ?? false,
      })),
    [problems]
  );

  const filters: ProblemFilters = useMemo(
    () => ({ difficulty: selectedDifficulty, category: selectedCategory }),
    [selectedDifficulty, selectedCategory]
  );

  const filteredProblems = useMemo(() => filterProblems(mappedProblems, filters), [mappedProblems, filters]);

  const filteredFullProblems = useMemo(() => {
    const filteredIds = new Set(filteredProblems.map((p) => p.id));
    const q = search.trim().toLowerCase();
    return problems.filter((p) => {
      if (!filteredIds.has(p.id)) return false;
      if (!q) return true;
      return p.title.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);
    });
  }, [problems, filteredProblems, search]);

  const overall = useMemo(() => {
    const total = mappedProblems.length;
    const completed = mappedProblems.filter((p) => p.isCompleted).length;
    return { total, completed, percentage: calculateProgress(completed, total) };
  }, [mappedProblems]);

  const perTier = useMemo(() => {
    const result: Record<Difficulty, { completed: number; total: number }> = {
      Beginner: { completed: 0, total: 0 },
      Intermediate: { completed: 0, total: 0 },
      Advanced: { completed: 0, total: 0 },
      Expert: { completed: 0, total: 0 },
    };
    mappedProblems.forEach((p) => {
      result[p.difficulty].total++;
      if (p.isCompleted) result[p.difficulty].completed++;
    });
    return result;
  }, [mappedProblems]);

  const clearFilters = () => {
    setSelectedDifficulty(undefined);
    setSelectedCategory(undefined);
    setSearch('');
  };

  const hasActiveFilters = Boolean(selectedDifficulty || selectedCategory || search.trim());

  if (loading) {
    return (
      <div className="nav-tab-page practice-detail-page lld-catalog-page min-h-screen pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-primary-500 animate-spin mb-3" />
            <p className="text-gray-600 dark:text-gray-400 text-sm">Loading problems…</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="nav-tab-page practice-detail-page lld-catalog-page min-h-screen pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col items-center justify-center py-24">
            <AlertCircle className="w-9 h-9 text-rose-500 mb-3" />
            <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="lld-page nav-tab-page practice-detail-page lld-catalog-page min-h-screen pt-16">
      {deleteTarget && (
        <DeleteConfirmDialog
          itemTitle={deleteTarget.title}
          deleting={deleting}
          error={deleteError}
          onConfirm={handleConfirmDelete}
          onCancel={() => {
            if (deleting) return;
            setDeleteTarget(null);
            setDeleteError(null);
          }}
        />
      )}
      <div className="lld-catalog-layout max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <PageHeader
          title="Low-Level Design Practice"
          description="Work through real interview problems — entities, relationships, UML and working code, with as much or as little guidance as you want."
          icon={Layers}
          iconColor="text-primary-600 dark:text-primary-400"
          gradientColors={{ from: 'from-primary-500', via: 'via-primary-600', to: 'to-primary-700' }}
        />

        {/* Progress summary — inline, not boxed */}
        <div className="lld-catalog-progress flex flex-wrap items-center gap-4 mb-8 text-sm">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 dark:text-white">{overall.completed}/{overall.total}</span>
            <span className="text-gray-600 dark:text-gray-400">solved</span>
          </div>
          <span className="w-px h-4 bg-gray-200 dark:bg-gray-700" />
          {DIFFICULTIES.map((d) => (
            <div key={d} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${difficultyDot(d)}`} />
              <span className="text-xs text-gray-600 dark:text-gray-400">{d}</span>
              <span className="text-xs text-gray-500 tabular-nums">{perTier[d].completed}/{perTier[d].total}</span>
            </div>
          ))}
        </div>

        {/* ─── Your problems (custom) ────────────────────────────────────── */}
        <section className="lld-custom-section mt-12 pt-8 border-t border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">
              <Sparkles className="w-3.5 h-3.5" /> Your problems
            </span>
            {!customLoading && !customError && customProblems.length > 0 && (
              <span className="text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-primary-100 text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 tabular-nums">
                {customProblems.length}
              </span>
            )}
          </div>

          {/* Secondary actions live below the curated catalog. */}
          {authState.isAuthenticated && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
              <Link
                to="/practice/lld/scratch/mode"
                className="group rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="card-accent-strip h-[3px] w-full bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors" />
                <div className="flex items-center gap-3.5 px-4 py-3.5">
                  <span className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 text-primary-600 dark:text-primary-400 flex items-center justify-center shrink-0 group-hover:border-primary-200 dark:group-hover:border-primary-800 transition-colors">
                    <Pencil className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      Start blank session
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      Empty workspace — design any prompt live
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-primary-500 shrink-0 transition-colors" />
                </div>
              </Link>

              <Link
                to="/practice/lld/custom/new"
                className="group rounded-xl border border-gray-200/80 dark:border-gray-700/80 bg-white dark:bg-gray-800 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 overflow-hidden flex flex-col"
              >
                <div className="card-accent-strip h-[3px] w-full bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors" />
                <div className="flex items-center gap-3.5 px-4 py-3.5">
                  <span className="w-10 h-10 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 text-primary-600 dark:text-primary-400 flex items-center justify-center shrink-0 group-hover:border-primary-200 dark:group-hover:border-primary-800 transition-colors">
                    <Plus className="w-4 h-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="text-sm font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      Author your own problem
                    </h3>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                      Save a reusable problem to your bank
                    </p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-300 dark:text-gray-600 group-hover:text-primary-500 shrink-0 transition-colors" />
                </div>
              </Link>
            </div>
          )}

          {customLoading ? (
            <div className="flex flex-col items-center justify-center py-12 rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
              <Loader2 className="w-6 h-6 text-primary-500 animate-spin mb-2" />
              <p className="text-gray-600 dark:text-gray-400 text-sm">Loading your problems…</p>
            </div>
          ) : customError ? (
            <div className="flex flex-col items-center justify-center py-12 rounded-2xl border border-rose-200 dark:border-rose-900/50 bg-rose-50/50 dark:bg-rose-900/10">
              <AlertCircle className="w-7 h-7 text-rose-500 mb-2" />
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{customError}</p>
              <button
                onClick={fetchCustom}
                className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
              >
                <RefreshCw className="w-4 h-4" /> Retry
              </button>
            </div>
          ) : customProblems.length === 0 ? null : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customProblems.map((problem) => {
                const CategoryIcon = problem.category ? getCategoryIcon(problem.category as Category) : Building;
                return (
                  <Link
                    key={problem.id}
                    to={`/practice/lld/${problem.id}/mode`}
                    className="group relative flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 shadow-md transition-all duration-200 hover:border-primary-300 dark:hover:border-primary-700 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary-400/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                    {/* Status row */}
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                        <Sparkles className="w-3 h-3" /> Custom
                      </span>
                      {problem.difficulty && (
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${difficultyBadge(problem.difficulty as Difficulty)}`}>
                          {problem.difficulty}
                        </span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white leading-snug mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                      {problem.title}
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2 mb-4 flex-1">
                      {problem.description}
                    </p>

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-800">
                      <span className="inline-flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-500 min-w-0">
                        <span className="w-6 h-6 rounded-md bg-primary-50 dark:bg-primary-900/20 flex items-center justify-center text-primary-500 dark:text-primary-400 flex-shrink-0">
                          <CategoryIcon className="w-3.5 h-3.5" />
                        </span>
                        <span className="truncate">{problem.category || 'Custom problem'}</span>
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigate(`/practice/lld/custom/${encodeURIComponent(problem.id)}/edit`);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-colors"
                          aria-label={`Edit ${problem.title}`}
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDeleteError(null);
                            setDeleteTarget(problem);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/30 transition-colors"
                          aria-label={`Delete ${problem.title}`}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        {/* ─── Search + filters ──────────────────────────────────────────── */}
        <div className="lld-catalog-controls mb-6 space-y-3">
          <div className="flex items-center justify-between gap-4 mb-4"><div><span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-600 dark:text-primary-400">Problem library</span><h2 className="mt-1 text-xl font-semibold text-gray-900 dark:text-white">Choose a design problem</h2></div><span className="text-xs text-gray-500">{filteredFullProblems.length} available</span></div>
          <div className="flex gap-2"><div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search problems…"
              className="w-full pl-10 pr-4 py-2.5 text-sm rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 placeholder-gray-500 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-400 dark:focus:border-primary-500 transition-shadow"
            />
          </div><button type="button" onClick={() => setShowFilters((value) => !value)} className={`inline-flex items-center gap-2 px-4 rounded-xl border text-sm font-medium transition-colors ${showFilters || selectedDifficulty || selectedCategory ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`}><SlidersHorizontal className="w-4 h-4" /> Filters{(selectedDifficulty || selectedCategory) && <span className="w-1.5 h-1.5 rounded-full bg-white" />}</button></div>

          {showFilters && <div className="lld-filter-panel grid gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white/50 dark:bg-gray-800/40">
            <div><span className="block mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Difficulty</span><div className="flex flex-wrap gap-2">
            {DIFFICULTIES.map((difficulty) => (
              <button
                key={difficulty}
                onClick={() => setSelectedDifficulty(selectedDifficulty === difficulty ? undefined : difficulty)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${selectedDifficulty === difficulty
                  ? 'bg-primary-600 text-white border-primary-600'
                  : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-700'}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${selectedDifficulty === difficulty ? 'bg-white' : difficultyDot(difficulty)}`} />
                {difficulty}
              </button>
            ))}
            </div></div><div><span className="block mb-2 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Category</span><div className="flex flex-wrap gap-2">
            {CATEGORIES.map((category) => {
              const Icon = getCategoryIcon(category);
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(selectedCategory === category ? undefined : category)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${selectedCategory === category
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-primary-300 dark:hover:border-primary-700'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {category}
                </button>
              );
            })}
            </div></div>{hasActiveFilters && (
              <button onClick={clearFilters} className="ml-auto text-xs text-primary-600 dark:text-primary-400 hover:underline underline-offset-2">
                Clear
              </button>
            )}
          </div>}
        </div>

        {/* ─── Cards or empty ────────────────────────────────────────────── */}
        <section className="lld-problem-library">{filteredFullProblems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
            <AlertCircle className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-600 dark:text-gray-400 mb-1">No problems match your filters</p>
            <p className="text-gray-500 dark:text-gray-500 text-sm mb-4">Try adjusting the search or filters</p>
            <button onClick={clearFilters} className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium">
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredFullProblems.map((problem) => {
              const isCompleted = problem.progress?.isCompleted ?? false;
              const inProgress = !isCompleted && !!problem.progress && !!problem.progress.mode;
              const CategoryIcon = getCategoryIcon(problem.category);

              return (
                <Link
                  key={problem.id}
                  to={`/practice/lld/${problem.id}/mode`}
                  className="group rounded-xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700/80 shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 flex flex-col overflow-hidden"
                >
                  {/* Top accent strip */}
                  <div className="card-accent-strip h-[3px] w-full bg-gray-200 dark:bg-gray-700 group-hover:bg-primary-400 dark:group-hover:bg-primary-500 transition-colors" />

                  <div className="p-5 flex flex-col flex-1">
                    {/* Icon + Title */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-11 h-11 rounded-xl bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0 group-hover:border-primary-200 dark:group-hover:border-primary-800 transition-colors">
                        <CategoryIcon className="w-5 h-5 text-primary-500 dark:text-primary-400" />
                      </div>
                      <div className="min-w-0">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white leading-snug group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {problem.title}
                        </h3>
                      </div>
                    </div>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2 mb-4">
                      {problem.description}
                    </p>

                    {/* Spacer */}
                    <div className="flex-1" />

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700 mt-auto">
                      <div className="flex items-center gap-2">
                        <span className={`inline-block text-[11px] font-semibold px-2.5 py-1 rounded-md ${difficultyBadge(problem.difficulty)}`}>
                          {problem.difficulty}
                        </span>
                        {isCompleted ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Solved
                          </span>
                        ) : inProgress ? (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
                            In progress
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center text-primary-600 dark:text-primary-400 text-sm font-medium group-hover:translate-x-0.5 transition-transform">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}</section>
      </div>
    </div>
  );
};

export default ProblemCatalog;
